// src/hooks/consult/useConsultMediaTelemetry.ts

import { useCallback, useEffect, useRef } from "react";

import { describeElement, describeTracks, PeerTelemetry, StatsBaseline, summarisePeerStats, WebrtcStatsPayload } from "../../utils/consult/mediaTelemetry";

export const TELEMETRY_INTERVAL_MS = 10_000;
export const STATE_CHANGE_MIN_GAP_MS = 2_000;
// talk keeps only the first 8 peers of a sample.
const MAX_PEERS = 8;

export interface TelemetrySocket {
	connected?: boolean;
	emit: (event: string, payload?: any) => void;
}

export interface TelemetryMesh {
	forEachPc: (callback: (pc: RTCPeerConnection, peerSocketId: string) => void) => void;
}

export interface UseConsultMediaTelemetryOptions {
	enabled: boolean;
	roomId: string;
	getSocket: () => TelemetrySocket | null | undefined;
	getMesh: () => TelemetryMesh | null | undefined;
	getLocalStream: () => MediaStream | null | undefined;
	getRemoteVideo: (peerSocketId: string) => HTMLVideoElement | null | undefined;
}

export interface ConsultMediaTelemetry {
	noteConnectionState: (peerSocketId: string, state: RTCPeerConnectionState) => void;
}

// One element per remote socket id; React refs reach through portals, so a pop-out window's <video> registers the same way.
export function useRemoteVideoRegistry() {
	const videosRef = useRef(new Map<string, HTMLVideoElement>());
	const registerRemoteVideo = useCallback((peerSocketId: string, element: HTMLVideoElement | null) => {
		if (element) videosRef.current.set(peerSocketId, element);
		else videosRef.current.delete(peerSocketId);
	}, []);
	const getRemoteVideo = useCallback((peerSocketId: string) => videosRef.current.get(peerSocketId) ?? null, []);
	return { registerRemoteVideo, getRemoteVideo };
}

export function useConsultMediaTelemetry(options: UseConsultMediaTelemetryOptions): ConsultMediaTelemetry {
	const optionsRef = useRef(options);
	optionsRef.current = options;

	const baselinesRef = useRef(new Map<string, { pc: RTCPeerConnection; baseline: StatsBaseline }>());
	const lastStateSampleRef = useRef(new Map<string, number>());
	// Bumped on disable/unmount so a getStats() still in flight can never emit afterwards.
	const generationRef = useRef(0);
	const activeRef = useRef(false);
	const warnedRef = useRef(false);

	const describePeer = useCallback(async (peerSocketId: string, pc: RTCPeerConnection | undefined, state: RTCPeerConnectionState | undefined, generation: number): Promise<PeerTelemetry | null> => {
		const connectionState = state ?? pc?.connectionState ?? "closed";
		// getStats() rejects on a closed connection, and the transition itself is the information.
		if (!pc || pc.connectionState === "closed") {
			baselinesRef.current.delete(peerSocketId);
			return { peer: peerSocketId, connectionState };
		}
		let report: RTCStatsReport;
		try {
			report = await pc.getStats();
		} catch (error) {
			if (!warnedRef.current) {
				warnedRef.current = true;
				console.warn("consult.telemetry.stats_failed", { errorName: (error as { name?: string } | null)?.name ?? "unknown" });
			}
			return null;
		}
		if (generation !== generationRef.current) return null;
		const previous = baselinesRef.current.get(peerSocketId);
		const { sample, baseline } = summarisePeerStats(report, previous?.pc === pc ? previous.baseline : undefined);
		baselinesRef.current.set(peerSocketId, { pc, baseline });

		const video = optionsRef.current.getRemoteVideo(peerSocketId);
		const entry: PeerTelemetry = { peer: peerSocketId, connectionState, ...sample };
		if (video) {
			entry.element = describeElement(video);
			const remoteTracks = describeTracks(video.srcObject as MediaStream | null);
			if (remoteTracks) entry.remoteTracks = remoteTracks;
		}
		return entry;
	}, []);

	const collect = useCallback(
		async (trigger: WebrtcStatsPayload["trigger"], only?: { peerSocketId: string; state: RTCPeerConnectionState }) => {
			const generation = generationRef.current;
			const mesh = optionsRef.current.getMesh();
			if (!mesh) return;

			const pcs = new Map<string, RTCPeerConnection>();
			mesh.forEachPc((pc, peerSocketId) => {
				if (!only || peerSocketId === only.peerSocketId) pcs.set(peerSocketId, pc);
			});
			if (!only) {
				for (const peerSocketId of Array.from(baselinesRef.current.keys())) {
					if (!pcs.has(peerSocketId)) baselinesRef.current.delete(peerSocketId);
				}
			}

			const targets: Array<[string, RTCPeerConnection | undefined]> = only ? [[only.peerSocketId, pcs.get(only.peerSocketId)]] : Array.from(pcs.entries()).slice(0, MAX_PEERS);
			const peers = (await Promise.all(targets.map(([peerSocketId, pc]) => describePeer(peerSocketId, pc, only?.state, generation)))).filter((peer): peer is PeerTelemetry => peer !== null);

			if (generation !== generationRef.current || !activeRef.current) return;
			if (only && peers.length === 0) return;
			const { roomId, getSocket, getLocalStream } = optionsRef.current;
			const socket = getSocket();
			if (!socket?.connected) return;
			const payload: WebrtcStatsPayload = { roomId, v: 1, kind: "webrtc_stats", trigger, local: describeTracks(getLocalStream()) ?? {}, peers };
			socket.emit("consultTelemetry", payload);
		},
		[describePeer],
	);

	useEffect(() => {
		if (!options.enabled) return;
		activeRef.current = true;
		const baselines = baselinesRef.current;
		const lastStateSample = lastStateSampleRef.current;
		const timer = setInterval(() => void collect("interval"), TELEMETRY_INTERVAL_MS);
		return () => {
			clearInterval(timer);
			activeRef.current = false;
			generationRef.current += 1;
			baselines.clear();
			lastStateSample.clear();
		};
	}, [options.enabled, collect]);

	const noteConnectionState = useCallback(
		(peerSocketId: string, state: RTCPeerConnectionState) => {
			if (!activeRef.current) return;
			const now = Date.now();
			const last = lastStateSampleRef.current.get(peerSocketId);
			if (last !== undefined && now - last < STATE_CHANGE_MIN_GAP_MS) return;
			lastStateSampleRef.current.set(peerSocketId, now);
			void collect("state_change", { peerSocketId, state });
		},
		[collect],
	);

	return { noteConnectionState };
}
